/**
 * SharedGamePage — landing route for /games/shared/:token.
 *
 * Calls accept-game-share with the URL token, then mounts the
 * DynamicGameRenderer with `sharedFromUserId` set so the play submission
 * credits the inviter.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { gameShareService } from '@/services/gameShareService';
import { DynamicGameRenderer } from '@/components/games/dynamic/DynamicGameRenderer';
import type { GameRunResult } from '@/types/gameSpec';

interface AcceptedGame {
  id: string;
  name: string;
  description?: string | null;
  game_config: unknown;
  grade_min: number;
  grade_max: number;
}

const SharedGamePage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [game, setGame] = useState<AcceptedGame | null>(null);
  const [sharedBy, setSharedBy] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Missing share token.');
      setLoading(false);
      return;
    }
    gameShareService
      .accept(token)
      .then((res) => {
        setGame(res.game as unknown as AcceptedGame);
        setSharedBy(res.shared_by_user_id);
      })
      .catch((e) =>
        setError(e.message ?? 'This share link is no longer valid.'),
      )
      .finally(() => setLoading(false));
  }, [token]);

  const handleComplete = (_result: GameRunResult) => {
    /* Persistence happens inside ResultScreen; nothing else to do here. */
  };

  if (loading) {
    return (
      <div className="container max-w-3xl py-8 space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="container max-w-2xl py-12">
        <Card>
          <CardHeader>
            <CardTitle>Share unavailable</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error ?? 'Game not found.'}</AlertDescription>
            </Alert>
            <Button onClick={() => navigate('/games')}>Browse games</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold">{game.name}</h1>
        {game.description && (
          <p className="text-muted-foreground">{game.description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          Shared with you · play to record your score
        </p>
      </div>
      <DynamicGameRenderer
        spec={game.game_config}
        gameId={game.id}
        grade={game.grade_min}
        sharedFromUserId={sharedBy}
        onComplete={handleComplete}
        onExit={() => navigate('/games')}
      />
    </div>
  );
};

export default SharedGamePage;
